import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import WeeklyRecapPreviewModal from "@/components/WeeklyRecapPreviewModal";


/**
 * Iter 87 batch-5 — compact relative-time formatter for the recent-user
 * chip timestamps. Returns "just now", "5m", "2h", "3d". Prefers short
 * over descriptive because it lives in a 9px pill.
 */
function formatRelativeTime(atMs) {
  if (!atMs) return "";
  const delta = Math.max(0, Date.now() - atMs);
  const s = Math.floor(delta / 1000);
  if (s < 45) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/**
 * Iter 87 — Rate-Limit Bucket Dashboard.
 *
 * Admin-only card that surfaces live state of every configured
 * rate-limit bucket (auth, backtest, explain, emit_flip, metrics)
 * so on-call ops can spot spikes before users hit 429s. Each
 * bucket row shows:
 *   • limit (max req / window)
 *   • active_keys (unique callers with any hits in the last window)
 *   • allowed_total / rejected_total (lifetime counters)
 *   • top 3 heaviest keys with used / remaining budget
 *   • one-click "Reset" button that clears the bucket via
 *     POST /admin/rate-limit/reset — powered by the same endpoint
 *     the Iter 87 pytest suite uses.
 *
 * Auto-refreshes every 10s so ops can watch a spike drain in real
 * time. Gated on `isAdmin` upstream (NotificationsPage).
 */
export default function RateLimitDashboardCard() {
  const [buckets, setBuckets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resettingBucket, setResettingBucket] = useState(null);
  // Iter 87 batch-3 — sparkline tooltip state (which bucket + bar idx).
  const [hoveredBar, setHoveredBar] = useState(null);
  // Iter 87 batch-4 — sparkline scale toggle (linear vs log1p).
  const [scaleMode, setScaleMode] = useState(() => {
    try { return localStorage.getItem("stratos.rl.spark-scale") || "linear"; }
    catch { return "linear"; }
  });
  // Iter 87 batch-3 — digest preview from dashboard.
  const [digestUserId, setDigestUserId] = useState("");
  const [digestOpen, setDigestOpen] = useState(false);
  // Iter 87 batch-4 — recent-user history for the digest preview
  // search (last 5, dedupe, most-recent-first). Persists across
  // reloads so debugging sessions don't lose context.
  // Iter 87 batch-5 — each entry is now ``{uid, at}`` so we can
  // render "5m ago" chips. Old string-only entries are auto-upgraded
  // to the new shape on first load with a null timestamp.
  const [recentDigestUsers, setRecentDigestUsers] = useState(() => {
    try {
      const raw = localStorage.getItem("stratos.rl.recent-digest-users");
      const parsed = raw ? JSON.parse(raw) : [];
      return parsed.slice(0, 5).map((entry) =>
        typeof entry === "string" ? { uid: entry, at: null } : entry
      );
    } catch { return []; }
  });
  // Iter 87 batch-3/4 — alert throttle config (global + per-bucket).
  const [threshold, setThreshold] = useState(null);
  const [thresholdInput, setThresholdInput] = useState("");
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [perBucketOverrides, setPerBucketOverrides] = useState({});
  const [perBucketInputs, setPerBucketInputs] = useState({});
  const [savingBucket, setSavingBucket] = useState(null);
  // Iter 87 batch-5 — flash-target bucket driven by the deep-link
  // SSE toast (setting via window event). Renders a 1200ms amber
  // ring on the row so ops can spot which bucket the toast was
  // pointing at.
  const [flashBucket, setFlashBucket] = useState(null);
  // Iter 87 batch-5 — inline threshold history drawer (per bucket).
  const [historyBucket, setHistoryBucket] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  // Iter 87 batch-6 — legend explainer popover (which bucket is open).
  const [legendPopover, setLegendPopover] = useState(null);
  // Iter 88 batch-1 — sticky-pin state for the legend popover so admins
  // can share the definition on screen-share without holding hover.
  // Click pins (mouseleave ignored); click again or Esc unpins.
  const [pinnedLegend, setPinnedLegend] = useState(null);
  // Iter 88 batch-1 — inline "Undo? [Yes]/[No]" confirmation state per
  // bucket. First ↶ click enters confirm mode + starts a 5s auto-reset
  // so a panic-response click can't accidentally roll back a change.
  const [undoConfirmBucket, setUndoConfirmBucket] = useState(null);
  // Iter 87 batch-6 — cached user display names for recent-preview chips.
  // Iter 88 batch-1 — TTL upgrade. Values are now ``{name, at}`` so
  // renamed accounts don't linger stale > 24h. Legacy string entries
  // are migrated on first read (treated as fresh — we don't know when
  // they were fetched so respect them until the next natural refresh).
  const [userNames, setUserNames] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("stratos.rl.user-names") || "{}");
      const migrated = {};
      Object.entries(raw).forEach(([uid, v]) => {
        if (typeof v === "string") {
          migrated[uid] = { name: v, at: Date.now() };
        } else if (v && typeof v === "object") {
          migrated[uid] = { name: v.name, at: v.at ?? Date.now() };
        }
      });
      return migrated;
    }
    catch { return {}; }
  });
  // Iter 88 batch-1 — pending Compliance Export All click.
  const [exportingAll, setExportingAll] = useState(false);
  // Iter 88 batch-2 — Audit Trail Search filters (bucket substring + actor email/uid).
  const [exportFilterBucket, setExportFilterBucket] = useState("");
  const [exportFilterActor, setExportFilterActor] = useState("");
  // Iter 88 batch-2 — Sparkline Zoom drill-down (which bucket + loaded bins).
  // Iter 88 batch-3 — Zoom Compare. Multi-open: track a Set of open
  // buckets + per-bucket bin data so admins can eyeball two spikes
  // side-by-side.
  const [openZooms, setOpenZooms] = useState(() => new Set());
  const [zoomBinsByBucket, setZoomBinsByBucket] = useState({});
  const [zoomLoadingSet, setZoomLoadingSet] = useState(() => new Set());
  // Iter 88 batch-3 — Search Presets. Last 3 {bucket, actor} combos
  // persisted so quarterly reviews can replay a common query in one
  // tap. LRU ordering: most-recently-exported at the front.
  const [exportPresets, setExportPresets] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("stratos.rl.export-presets") || "[]");
      return Array.isArray(raw) ? raw.slice(0, 3) : [];
    } catch { return []; }
  });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/rate-limit/state");
      setBuckets(r.data?.buckets || []);
      setError("");
    } catch (e) {
      // 403 for non-admins is expected — card hides itself upstream.
      // Any other failure surfaces so ops know the endpoint is down.
      setError(e?.response?.data?.detail || e?.message || "Load failed");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadThreshold = useCallback(async () => {
    try {
      const r = await api.get("/admin/bucket-alerts/threshold");
      setThreshold(r.data?.threshold);
      setThresholdInput(String(r.data?.threshold ?? ""));
      setPerBucketOverrides(r.data?.per_bucket || {});
    } catch (_) {
      // Silent — the section just won't render if the endpoint is 403.
    }
  }, []);

  useEffect(() => {
    load();
    loadThreshold();
    const t = setInterval(load, 10000);
    // Iter 87 batch-5 — Spike Toast Deep-Link. Listens for a global
    // ``stratos:rate-limit-focus`` custom event dispatched by the
    // Sidebar toast onClick. Scrolls the target row into view and
    // flashes its border for 1200ms.
    const onFocusEvent = (e) => {
      const bucket = e?.detail?.bucket;
      if (!bucket) return;
      const el = document.querySelector(`[data-testid='rate-limit-row-${bucket}']`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setFlashBucket(bucket);
      setTimeout(() => setFlashBucket((cur) => (cur === bucket ? null : cur)), 1200);
    };
    window.addEventListener("stratos:rate-limit-focus", onFocusEvent);
    // Iter 87 batch-5 — hash-based deep link (Sidebar toast can
    // navigate here first, then dispatch the focus event). If the
    // hash carries a bucket name, focus it once buckets are loaded.
    if (typeof window !== "undefined" && window.location.hash) {
      const m = window.location.hash.match(/^#rate-limits(?::(\w+))?/);
      if (m && m[1]) {
        setTimeout(() => onFocusEvent({ detail: { bucket: m[1] } }), 400);
      }
    }
    return () => {
      clearInterval(t);
      window.removeEventListener("stratos:rate-limit-focus", onFocusEvent);
    };
  }, [load, loadThreshold]);

  // Iter 88 batch-3 — Ops Shift Handoff heartbeat. Pings peers every
  // 30s while the card is mounted so their Sidebar co-viewer pill
  // reflects who's on the dashboard right now. First ping fires
  // immediately after mount so peers see us within a second, not 30s.
  useEffect(() => {
    let cancelled = false;
    const ping = () => {
      if (cancelled) return;
      try {
        const p = api.post("/admin/ops/heartbeat", { page: "rate-limits" });
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch {}
    };
    ping();
    const t = setInterval(ping, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const handleReset = async (bucketName) => {
    setResettingBucket(bucketName);
    try {
      const r = await api.post("/admin/rate-limit/reset", { bucket: bucketName });
      toast.success(`Cleared ${bucketName}`, {
        description: `${r.data?.memory_cleared || 0} in-memory keys removed`,
      });
      // Immediate refetch so the row visibly clears without waiting 10s.
      await load();
    } catch (e) {
      toast.error(`Reset failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setResettingBucket(null);
    }
  };

  const handleSaveThreshold = async () => {
    const val = parseInt(thresholdInput, 10);
    if (!Number.isFinite(val) || val < 1 || val > 10000) {
      toast.error("Threshold must be an integer between 1 and 10000");
      return;
    }
    setSavingThreshold(true);
    try {
      const r = await api.put("/admin/bucket-alerts/threshold", { threshold: val });
      setThreshold(r.data?.threshold);
      toast.success(`Threshold set to ${r.data?.threshold}`, {
        description: "Takes effect on the next 60s tick",
      });
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setSavingThreshold(false);
    }
  };

  const handlePreviewDigest = () => {
    const uid = digestUserId.trim();
    if (!uid) {
      toast.error("Enter a user ID (or leave blank to preview your own)");
      return;
    }
    // Iter 87 batch-4/5 — remember recent previews as ``{uid, at}``
    // so admins get "5m ago" hints alongside the chip.
    const nowMs = Date.now();
    setRecentDigestUsers((prev) => {
      const filtered = prev.filter((e) => e.uid !== uid);
      const next = [{ uid, at: nowMs }, ...filtered].slice(0, 5);
      try { localStorage.setItem("stratos.rl.recent-digest-users", JSON.stringify(next)); }
      catch {}
      return next;
    });
    setDigestOpen(true);
  };

  // Iter 87 batch-6 — resolve names for any unresolved recent-user
  // ObjectIds. Runs once per new-uid batch and caches to
  // localStorage so we don't refetch on every mount.
  //
  // Iter 88 batch-1 — TTL. Entries older than 24h are considered stale
  // and re-fetched so display names track renames without an infinite
  // localStorage lifetime. TTL is bucket-uniform: one clock per uid.
  useEffect(() => {
    const NAME_TTL_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const unresolved = recentDigestUsers
      .map((e) => e.uid)
      .filter((uid) => {
        const entry = userNames[uid];
        if (!entry) return true;
        if (typeof entry === "string") return true; // Should have been migrated, but be safe.
        if (!entry.at) return true;
        return (now - entry.at) > NAME_TTL_MS;
      });
    if (unresolved.length === 0) return;
    let cancelled = false;
    api.get(`/admin/users/resolve?ids=${encodeURIComponent(unresolved.join(","))}`)
      .then((r) => {
        if (cancelled) return;
        const fetchedAt = Date.now();
        const additions = {};
        (r.data?.users || []).forEach((u) => {
          additions[u.id] = { name: u.name, at: fetchedAt };
        });
        // Mark unresolved-but-not-in-response as "unknown" so we don't
        // spam the endpoint on each render. Timestamp so TTL still applies
        // (in case the user is created later).
        unresolved.forEach((uid) => {
          if (!additions[uid]) additions[uid] = { name: "unknown", at: fetchedAt };
        });
        const next = { ...userNames, ...additions };
        setUserNames(next);
        try { localStorage.setItem("stratos.rl.user-names", JSON.stringify(next)); } catch {}
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [recentDigestUsers, userNames]);

  const handleExportHistoryCsv = () => {
    if (!historyBucket || historyEntries.length === 0) {
      toast.error("Nothing to export — load a bucket's history first");
      return;
    }
    const rows = [
      ["bucket", "scope", "from", "to", "changed_by", "changed_at"],
      ...historyEntries.map((h) => [
        h.bucket ?? "",
        h.scope ?? "",
        h.from == null ? "" : String(h.from),
        h.to == null ? "global" : String(h.to),
        h.changed_by ?? "",
        h.changed_at ?? "",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => {
      const s = String(c);
      // RFC 4180 — quote fields containing commas/quotes/newlines.
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `threshold-history-${historyBucket}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${historyEntries.length} row${historyEntries.length === 1 ? "" : "s"}`);
  };

  const handleUndoThresholdChange = async (bucketName) => {
    // Newest entry is at index 0. "Undo" = restore ``from`` value.
    const latest = historyEntries[0];
    if (!latest) {
      toast.error("No history entry to undo");
      // Exit confirm mode either way — no history == nothing to defend.
      setUndoConfirmBucket(null);
      return;
    }
    try {
      if (latest.from == null) {
        // Undoing a "created override" → clear the override entirely.
        await api.delete(`/admin/bucket-alerts/threshold/${bucketName}`);
        setPerBucketOverrides((prev) => {
          const next = { ...prev }; delete next[bucketName]; return next;
        });
        toast.success(`Cleared ${bucketName} override`, {
          description: `Rolled back — falls back to global`,
        });
      } else if (latest.scope === "global") {
        // Undo a global change.
        await api.put("/admin/bucket-alerts/threshold", { threshold: latest.from });
        setThreshold(latest.from);
        setThresholdInput(String(latest.from));
        toast.success(`Restored global threshold to ${latest.from}`);
      } else {
        // Undo a per-bucket change (from is the prior value; could be
        // the global fallback if this was the first override).
        await api.put("/admin/bucket-alerts/threshold",
          { threshold: latest.from, bucket: bucketName });
        setPerBucketOverrides((prev) => ({ ...prev, [bucketName]: latest.from }));
        toast.success(`Restored ${bucketName} to ${latest.from}`);
      }
      // Refresh the history in-place so the undo row appears at the top.
      const query = `?bucket=${encodeURIComponent(bucketName)}&limit=10`;
      const r = await api.get(`/admin/bucket-alerts/history${query}`);
      setHistoryEntries(r.data?.history || []);
    } catch (e) {
      toast.error(`Undo failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      // Iter 88 batch-1 — always exit confirm mode after the attempt.
      setUndoConfirmBucket(null);
    }
  };

  // Iter 88 batch-1 — Undo Confirmation. First ↶ click enters "confirm"
  // mode (renders inline Yes/No pair + starts a 5s auto-cancel timer).
  // Guards accidental rollbacks during a panic-response window where an
  // ops person might be clicking through several rows quickly.
  const handleUndoClick = (bucketName) => {
    if (undoConfirmBucket === bucketName) {
      // Already in confirm mode — Yes-arm only fires from the explicit
      // Yes button. A second undo click cancels for parity with Esc.
      setUndoConfirmBucket(null);
      return;
    }
    setUndoConfirmBucket(bucketName);
    // Auto-dismiss so a stray click doesn't linger in confirm state.
    setTimeout(() => {
      setUndoConfirmBucket((cur) => (cur === bucketName ? null : cur));
    }, 5000);
  };

  const handleLoadHistory = async (bucketName) => {
    if (historyBucket === bucketName) {
      setHistoryBucket(null);
      setHistoryEntries([]);
      return;
    }
    try {
      const query = bucketName === "__global__" ? "?bucket=__global__&limit=10" : `?bucket=${encodeURIComponent(bucketName)}&limit=10`;
      const r = await api.get(`/admin/bucket-alerts/history${query}`);
      setHistoryBucket(bucketName);
      setHistoryEntries(r.data?.history || []);
    } catch (e) {
      toast.error(`History load failed: ${e?.response?.data?.detail || e?.message}`);
    }
  };

  const handleExportAllHistory = async () => {
    setExportingAll(true);
    try {
      // Iter 88 batch-2 — pass optional Audit Trail Search filters.
      const params = new URLSearchParams();
      if (exportFilterBucket.trim()) params.set("bucket", exportFilterBucket.trim());
      if (exportFilterActor.trim()) params.set("actor", exportFilterActor.trim());
      const qs = params.toString() ? `?${params.toString()}` : "";
      const r = await api.get(`/admin/bucket-alerts/history/export-all${qs}`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const parts = ["threshold-history"];
      if (exportFilterBucket.trim()) parts.push(`bucket-${exportFilterBucket.trim()}`);
      if (exportFilterActor.trim()) parts.push(`actor-${exportFilterActor.trim().split("@")[0]}`);
      parts.push(new Date().toISOString().slice(0, 10));
      a.download = `${parts.join("-")}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const scope = (exportFilterBucket || exportFilterActor)
        ? "filtered"
        : "all threshold history";
      toast.success(`Exported ${scope}`, {
        description: [
          exportFilterBucket && `bucket: ${exportFilterBucket}`,
          exportFilterActor && `actor: ${exportFilterActor}`,
        ].filter(Boolean).join(" · ") || "Every bucket · full audit trail",
      });
      // Iter 88 batch-3 — remember this combo as a preset for one-tap replay.
      savePreset(exportFilterBucket, exportFilterActor);
    } catch (e) {
      toast.error(`Export all failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setExportingAll(false);
    }
  };

  // Iter 88 batch-2 — Sparkline Zoom Drill-Down. Iter 88 batch-3 —
  // Zoom Compare. Multi-open: click a bar on a closed bucket opens
  // the drawer; click a bar on an already-open bucket refreshes its
  // bin data so admins can compare spikes side-by-side. The × in
  // each drawer's header removes just that entry.
  const handleOpenZoom = async (bucketName) => {
    setZoomLoadingSet((prev) => new Set(prev).add(bucketName));
    setOpenZooms((prev) => new Set(prev).add(bucketName));
    try {
      const r = await api.get(
        `/admin/rate-limit/sparkline-extended/${encodeURIComponent(bucketName)}`,
      );
      setZoomBinsByBucket((prev) => ({ ...prev, [bucketName]: r.data?.bins || [] }));
    } catch (e) {
      toast.error(`Zoom failed: ${e?.response?.data?.detail || e?.message}`);
      setOpenZooms((prev) => {
        const next = new Set(prev); next.delete(bucketName); return next;
      });
    } finally {
      setZoomLoadingSet((prev) => {
        const next = new Set(prev); next.delete(bucketName); return next;
      });
    }
  };

  const handleCloseZoom = (bucketName) => {
    setOpenZooms((prev) => {
      const next = new Set(prev); next.delete(bucketName); return next;
    });
    setZoomBinsByBucket((prev) => {
      const next = { ...prev }; delete next[bucketName]; return next;
    });
  };

  // Iter 88 batch-3 — persist the current filter combo as a search
  // preset. Called on every successful export. Empty combos (both
  // fields blank) are skipped so a plain "Export ALL" doesn't
  // pollute the recents. Duplicates dedupe on ``bucket|actor`` key.
  const savePreset = (bucket, actor) => {
    const b = (bucket || "").trim();
    const a = (actor || "").trim();
    if (!b && !a) return;
    const key = `${b}|${a}`;
    setExportPresets((prev) => {
      const filtered = prev.filter((p) => `${p.bucket || ""}|${p.actor || ""}` !== key);
      const next = [{ bucket: b, actor: a }, ...filtered].slice(0, 3);
      try { localStorage.setItem("stratos.rl.export-presets", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const handleApplyPreset = (preset) => {
    setExportFilterBucket(preset.bucket || "");
    setExportFilterActor(preset.actor || "");
  };

  const handleRemovePreset = (preset) => {
    setExportPresets((prev) => {
      const next = prev.filter((p) =>
        `${p.bucket || ""}|${p.actor || ""}` !== `${preset.bucket || ""}|${preset.actor || ""}`
      );
      try { localStorage.setItem("stratos.rl.export-presets", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const handleToggleScale = () => {
    setScaleMode((cur) => {
      const next = cur === "linear" ? "log" : "linear";
      try { localStorage.setItem("stratos.rl.spark-scale", next); } catch {}
      return next;
    });
  };

  const handleSaveBucketThreshold = async (bucketName) => {
    const raw = perBucketInputs[bucketName] ?? "";
    const val = parseInt(raw, 10);
    if (!Number.isFinite(val) || val < 1 || val > 10000) {
      toast.error("Bucket threshold must be an integer between 1 and 10000");
      return;
    }
    setSavingBucket(bucketName);
    try {
      const r = await api.put("/admin/bucket-alerts/threshold",
        { threshold: val, bucket: bucketName });
      setPerBucketOverrides((prev) => ({ ...prev, [bucketName]: r.data?.threshold }));
      toast.success(`${bucketName} threshold set to ${r.data?.threshold}`, {
        description: "Takes effect on the next 60s tick",
      });
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setSavingBucket(null);
    }
  };

  const handleClearBucketOverride = async (bucketName) => {
    setSavingBucket(bucketName);
    try {
      await api.delete(`/admin/bucket-alerts/threshold/${bucketName}`);
      setPerBucketOverrides((prev) => {
        const next = { ...prev };
        delete next[bucketName];
        return next;
      });
      setPerBucketInputs((prev) => {
        const next = { ...prev };
        delete next[bucketName];
        return next;
      });
      toast.success(`${bucketName} override cleared`, {
        description: `Falls back to global (${threshold}/60s)`,
      });
    } catch (e) {
      toast.error(`Clear failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setSavingBucket(null);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="rate-limit-dashboard-card"
        className="rounded-sm border border-zinc-800 bg-zinc-950 p-4"
      >
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
          Rate Limits · Loading…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="rate-limit-dashboard-card"
        className="rounded-sm border border-red-900/50 bg-zinc-950 p-4"
      >
        <div className="text-[10px] uppercase tracking-widest text-red-400">
          Rate Limits · {error}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="rate-limit-dashboard-card"
      className="rounded-sm border border-zinc-800 bg-zinc-950 p-4 space-y-3"
    >
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 md:pt-1">
          Rate Limits · Live
        </div>
        <div className="flex flex-col gap-1 md:items-end">
          <div className="flex flex-wrap items-center gap-2">
            {/* Iter 88 batch-2 — Audit Trail Search. Two thin inputs
                scope the CSV export to matching rows so annual reviews
                stay clean. Empty inputs preserve the "export all" behavior. */}
            <Input
              data-testid="rate-limit-export-filter-bucket"
              placeholder="filter: bucket"
              value={exportFilterBucket}
              onChange={(e) => setExportFilterBucket(e.target.value)}
              className="h-5 w-28 border-zinc-800 bg-zinc-950 text-[9px] font-mono placeholder:text-zinc-600"
            />
            <Input
              data-testid="rate-limit-export-filter-actor"
              placeholder="filter: actor email"
              value={exportFilterActor}
              onChange={(e) => setExportFilterActor(e.target.value)}
              className="h-5 w-40 border-zinc-800 bg-zinc-950 text-[9px] font-mono placeholder:text-zinc-600"
            />
            <Button
              data-testid="rate-limit-export-all-history"
              size="sm"
              variant="outline"
              disabled={exportingAll}
              onClick={handleExportAllHistory}
              className="h-5 border-zinc-800 bg-zinc-950 px-1.5 text-[8px] uppercase tracking-wider text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
              aria-label="Export the full threshold-history audit trail for all buckets as CSV"
            >
              {exportingAll ? "…" : (exportFilterBucket || exportFilterActor) ? "Export filtered (CSV)" : "Export ALL history (CSV)"}
            </Button>
            <Button
              data-testid="rate-limit-scale-toggle"
              size="sm"
              variant="outline"
              onClick={handleToggleScale}
              className="h-5 border-zinc-800 bg-zinc-950 px-1.5 text-[8px] uppercase tracking-wider text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
              aria-label={`Sparkline scale: ${scaleMode}. Click to toggle.`}
            >
              scale: {scaleMode}
            </Button>
            <div className="text-[9px] text-zinc-600 tabular-nums">
              {buckets.length} buckets · refreshes every 10s
            </div>
          </div>
          {/* Iter 88 batch-3 — Search Presets. Last 3 exported combos
              persist so quarterly reviews replay in one tap. */}
          {exportPresets.length > 0 && (
            <div
              data-testid="rate-limit-export-presets"
              className="flex flex-wrap gap-1 md:justify-end"
            >
              <span className="text-[8px] uppercase tracking-wider text-zinc-600 self-center mr-1">
                recents:
              </span>
              {exportPresets.map((p, i) => {
                const key = `${p.bucket || ""}|${p.actor || ""}`;
                const label = [
                  p.bucket && `bucket:${p.bucket}`,
                  p.actor && `actor:${p.actor.split("@")[0]}`,
                ].filter(Boolean).join(" · ");
                return (
                  <div
                    key={key}
                    data-testid={`rate-limit-export-preset-${i}`}
                    className="flex items-center gap-0.5 rounded-sm border border-zinc-800 bg-zinc-950 pl-1.5 font-mono text-[9px] text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
                  >
                    <button
                      data-testid={`rate-limit-export-preset-apply-${i}`}
                      onClick={() => handleApplyPreset(p)}
                      className="py-0.5 pr-1"
                      title="Restore this bucket + actor combo"
                    >
                      {label}
                    </button>
                    <button
                      data-testid={`rate-limit-export-preset-remove-${i}`}
                      onClick={() => handleRemovePreset(p)}
                      className="pl-0.5 pr-1.5 py-0.5 text-zinc-600 hover:text-red-400"
                      aria-label="Remove preset"
                      title="Remove preset"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {buckets.map((b) => {
          const utilization = b.limit > 0
            ? Math.min(100, Math.round(((b.top_keys[0]?.used || 0) / b.limit) * 100))
            : 0;
          const barTint = utilization >= 90
            ? "bg-red-500/70"
            : utilization >= 60
            ? "bg-amber-500/70"
            : "bg-emerald-500/70";
          return (
            <div
              key={b.name}
              data-testid={`rate-limit-row-${b.name}`}
              data-flash={flashBucket === b.name ? "true" : "false"}
              className={`rounded-sm border p-3 space-y-2 transition-all duration-300 ${
                flashBucket === b.name
                  ? "border-amber-400/80 bg-amber-500/10 shadow-[0_0_0_1px_rgba(251,191,36,0.5)]"
                  : "border-zinc-800/70 bg-zinc-900/40"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] text-zinc-200">
                    {b.name}
                  </span>
                  <span
                    data-testid={`rate-limit-limit-${b.name}`}
                    className="text-[9px] tabular-nums text-zinc-500"
                  >
                    {b.limit}/{b.window}s
                  </span>
                </div>
                <Button
                  data-testid={`rate-limit-reset-${b.name}`}
                  size="sm"
                  variant="outline"
                  disabled={resettingBucket === b.name}
                  onClick={() => handleReset(b.name)}
                  className="h-6 border-zinc-800 bg-zinc-950 px-2 text-[9px] uppercase tracking-wider text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
                >
                  {resettingBucket === b.name ? "…" : "Reset"}
                </Button>
              </div>

              {/* Utilization bar — reflects the heaviest key's usage
                  against the limit. Green <60%, amber 60–89%, red 90%+. */}
              <div className="h-1 w-full rounded-full bg-zinc-800/60">
                <div
                  data-testid={`rate-limit-util-${b.name}`}
                  data-utilization={utilization}
                  className={`h-full rounded-full transition-all duration-500 ${barTint}`}
                  style={{ width: `${utilization}%` }}
                />
              </div>

              {/* Iter 87 — 60s spike-shape sparkline. Bars indicate
                  rejected-per-5s (red) stacked on allowed-per-5s (zinc).
                  Iter 87 batch-3 — hovering any bar reveals a tooltip
                  with exact allowed/rejected counts and the 5s window.
                  Iter 87 batch-4 — bar heights honor the global
                  scaleMode toggle so a single 1000-req spike doesn't
                  flatten every other bar to invisibility. */}
              {b.sparkline && b.sparkline.length > 0 && (
                <div className="relative">
                  <div
                    data-testid={`rate-limit-sparkline-${b.name}`}
                    data-samples={b.sparkline.length}
                    data-scale={scaleMode}
                    className="flex items-end gap-[2px] h-8"
                    aria-label={`${b.name} activity over the last 60 seconds (${scaleMode} scale)`}
                  >
                    {b.sparkline.map((s, i) => {
                      const total = (s.allowed_delta || 0) + (s.rejected_delta || 0);
                      const maxTotal = Math.max(
                        1,
                        ...b.sparkline.map(x => (x.allowed_delta || 0) + (x.rejected_delta || 0))
                      );
                      // Iter 87 batch-4 — log1p normalizes wide dynamic
                      // ranges (1 vs 1000 becomes 0.15 vs 1.0 instead of
                      // 0.001 vs 1.0). Guard maxTotal==0 → division safe.
                      const heightPct = scaleMode === "log"
                        ? (Math.log1p(total) / Math.log1p(maxTotal)) * 100
                        : (total / maxTotal) * 100;
                      const rejFrac = total > 0 ? s.rejected_delta / total : 0;
                      const isHover = hoveredBar && hoveredBar.bucket === b.name && hoveredBar.idx === i;
                      return (
                        <div
                          key={i}
                          data-testid={`rate-limit-spark-bar-${b.name}-${i}`}
                          className={`flex-1 min-w-[3px] flex flex-col justify-end cursor-pointer transition-opacity ${isHover ? "" : "hover:opacity-80"}`}
                          style={{ height: "100%" }}
                          onMouseEnter={() => setHoveredBar({ bucket: b.name, idx: i, sample: s })}
                          onMouseLeave={() => setHoveredBar(null)}
                          onFocus={() => setHoveredBar({ bucket: b.name, idx: i, sample: s })}
                          onBlur={() => setHoveredBar(null)}
                          onClick={() => handleOpenZoom(b.name)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleOpenZoom(b.name);
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label={`Bar ${i + 1} of ${b.sparkline.length}: ${s.allowed_delta} allowed, ${s.rejected_delta} rejected. Click to open 5-minute drill-down.`}
                        >
                          <div style={{ height: `${heightPct}%` }} className="flex flex-col">
                            {rejFrac > 0 && (
                              <div
                                className="w-full bg-red-500/80"
                                style={{ height: `${rejFrac * 100}%` }}
                              />
                            )}
                            <div
                              className={`w-full flex-1 ${isHover ? "bg-zinc-400" : "bg-zinc-500/70"}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {hoveredBar && hoveredBar.bucket === b.name && (
                    <div
                      data-testid={`rate-limit-spark-tooltip-${b.name}`}
                      className="absolute -top-14 left-1/2 -translate-x-1/2 z-10 rounded-sm border border-zinc-700 bg-zinc-950 px-2 py-1 text-[9px] font-mono text-zinc-200 shadow-lg whitespace-nowrap pointer-events-none"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-emerald-400">+{hoveredBar.sample.allowed_delta} allowed</span>
                        <span className="text-red-400">{hoveredBar.sample.rejected_delta} rejected</span>
                      </div>
                      <div className="text-zinc-500 text-[8px] tabular-nums">
                        bar {hoveredBar.idx + 1}/12 · {(60 - hoveredBar.idx * 5)}–{(60 - (hoveredBar.idx + 1) * 5)}s ago · click to zoom
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Iter 88 batch-2 — Sparkline Zoom Drill-Down drawer.
                  Renders a 5-min / 60-bin history chart so ops can
                  investigate the spike context that led up to the
                  60s window. Same bar semantics: zinc = allowed,
                  red = rejected, tallest bar normalized to 100%.
                  Iter 88 batch-3 — supports multi-open so admins
                  can eyeball two spikes side-by-side. */}
              {openZooms.has(b.name) && (
                <div
                  data-testid={`rate-limit-zoom-drawer-${b.name}`}
                  className="rounded-sm border border-amber-500/40 bg-amber-500/5 p-2 space-y-1"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[9px] uppercase tracking-widest text-amber-400">
                      Zoom · last 5 min · {(zoomBinsByBucket[b.name] || []).length} × 5s bins
                    </div>
                    <Button
                      data-testid={`rate-limit-zoom-close-${b.name}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleCloseZoom(b.name)}
                      className="h-4 border-zinc-800 bg-zinc-950 px-1 text-[8px] uppercase tracking-wider text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200"
                    >
                      ×
                    </Button>
                  </div>
                  {zoomLoadingSet.has(b.name) ? (
                    <div className="text-[9px] text-zinc-500 italic">Loading…</div>
                  ) : (zoomBinsByBucket[b.name] || []).length === 0 ? (
                    <div className="text-[9px] text-zinc-600 italic">No activity in the last 5 minutes.</div>
                  ) : (
                    <div
                      data-testid={`rate-limit-zoom-chart-${b.name}`}
                      data-bins={(zoomBinsByBucket[b.name] || []).length}
                      className="flex items-end gap-[1px] h-12"
                      aria-label={`${b.name} activity over the last 5 minutes`}
                    >
                      {(zoomBinsByBucket[b.name] || []).map((z, i) => {
                        const bins = zoomBinsByBucket[b.name] || [];
                        const total = (z.allowed_delta || 0) + (z.rejected_delta || 0);
                        const maxTotal = Math.max(
                          1,
                          ...bins.map((x) => (x.allowed_delta || 0) + (x.rejected_delta || 0)),
                        );
                        const heightPct = scaleMode === "log"
                          ? (Math.log1p(total) / Math.log1p(maxTotal)) * 100
                          : (total / maxTotal) * 100;
                        const rejFrac = total > 0 ? z.rejected_delta / total : 0;
                        return (
                          <div
                            key={i}
                            data-testid={`rate-limit-zoom-bar-${b.name}-${i}`}
                            className="flex-1 min-w-[2px] flex flex-col justify-end"
                            style={{ height: "100%" }}
                            title={`+${z.allowed_delta} allowed · ${z.rejected_delta} rejected`}
                          >
                            <div style={{ height: `${heightPct}%` }} className="flex flex-col">
                              {rejFrac > 0 && (
                                <div className="w-full bg-red-500/80" style={{ height: `${rejFrac * 100}%` }} />
                              )}
                              <div className="w-full flex-1 bg-zinc-500/70" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Iter 87 batch-5 — sparkline legend. Iter 87 batch-6
                  — hovering the legend opens an inline info popover
                  explaining the 5s binning + log vs linear math.
                  Iter 88 batch-1 — clicking the legend PINS the
                  popover (mouseleave ignored while pinned) so admins
                  can share the definition on a screen-share. Click
                  again or press Esc to unpin. */}
              {b.sparkline && b.sparkline.length > 0 && (
                <div className="relative">
                  <div
                    data-testid={`rate-limit-sparkline-legend-${b.name}`}
                    data-pinned={pinnedLegend === b.name ? "true" : "false"}
                    className="flex items-center gap-2 text-[8px] uppercase tracking-wider text-zinc-600 cursor-help"
                    onMouseEnter={() => setLegendPopover(b.name)}
                    onMouseLeave={() => {
                      // Iter 88 batch-1 — respect the pin.
                      if (pinnedLegend !== b.name) setLegendPopover(null);
                    }}
                    onFocus={() => setLegendPopover(b.name)}
                    onBlur={() => {
                      if (pinnedLegend !== b.name) setLegendPopover(null);
                    }}
                    onClick={() => {
                      setPinnedLegend((cur) => {
                        const next = cur === b.name ? null : b.name;
                        // Keep the popover visible when pinning; hide
                        // it when explicitly unpinning via re-click.
                        setLegendPopover(next);
                        return next;
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Escape" && pinnedLegend === b.name) {
                        setPinnedLegend(null);
                        setLegendPopover(null);
                      } else if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setPinnedLegend((cur) => {
                          const next = cur === b.name ? null : b.name;
                          setLegendPopover(next);
                          return next;
                        });
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-pressed={pinnedLegend === b.name}
                    aria-label={`Sparkline legend for ${b.name}. Click to ${pinnedLegend === b.name ? "unpin" : "pin"} the explainer.`}
                  >
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2 h-2 bg-zinc-500/70 rounded-sm" />
                      allowed
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2 h-2 bg-red-500/80 rounded-sm" />
                      rejected
                    </span>
                    <span className="ml-auto text-zinc-700 flex items-center gap-1">
                      {scaleMode === "log" ? "log-scaled" : "linear"} · 60s
                      {pinnedLegend === b.name && (
                        <span
                          data-testid={`rate-limit-sparkline-legend-pin-${b.name}`}
                          className="text-amber-400"
                          aria-hidden="true"
                        >
                          📌
                        </span>
                      )}
                    </span>
                  </div>
                  {legendPopover === b.name && (
                    <div
                      data-testid={`rate-limit-sparkline-legend-popover-${b.name}`}
                      data-pinned={pinnedLegend === b.name ? "true" : "false"}
                      role="tooltip"
                      className={`absolute top-full left-0 right-0 mt-1 z-20 rounded-sm border bg-zinc-950 p-2 text-[9px] text-zinc-300 shadow-lg ${
                        pinnedLegend === b.name
                          ? "border-amber-500/60 pointer-events-auto"
                          : "border-zinc-700 pointer-events-none"
                      }`}
                    >
                      <div className="space-y-1">
                        <div>
                          <span className="text-zinc-500 uppercase tracking-wider">Bins </span>
                          <span>12 × 5s over the last 60s. Each bar counts requests
                            recorded during that 5-second window.</span>
                        </div>
                        <div>
                          <span className="text-zinc-500 uppercase tracking-wider">Stacking </span>
                          <span>zinc = allowed requests; red on top = rejected
                            (429s). Height per bar is normalized to the tallest
                            bar in this row so quiet buckets stay visible.</span>
                        </div>
                        <div>
                          <span className="text-zinc-500 uppercase tracking-wider">Scale </span>
                          <span>{scaleMode === "log"
                            ? "log-scaled: bar height = log1p(total) / log1p(max) · 100. Smooths wide dynamic ranges (1 vs 1000)."
                            : "linear: bar height = total / max · 100. Direct proportion — larger spikes dwarf smaller bars."}</span>
                        </div>
                        {pinnedLegend === b.name && (
                          <div className="pt-1 border-t border-zinc-800 text-[8px] text-zinc-500 uppercase tracking-wider">
                            Pinned · click legend or press Esc to close
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 text-[9px]">
                <div>
                  <div className="text-zinc-500 uppercase tracking-wider">Active</div>
                  <div
                    data-testid={`rate-limit-active-${b.name}`}
                    className="tabular-nums text-zinc-300"
                  >
                    {b.active_keys}
                  </div>
                </div>
                <div>
                  <div className="text-emerald-500/70 uppercase tracking-wider">Allowed</div>
                  <div
                    data-testid={`rate-limit-allowed-${b.name}`}
                    className="tabular-nums text-emerald-400"
                  >
                    {b.allowed_total}
                  </div>
                </div>
                <div>
                  <div className="text-red-500/70 uppercase tracking-wider">Rejected</div>
                  <div
                    data-testid={`rate-limit-rejected-${b.name}`}
                    className="tabular-nums text-red-400"
                  >
                    {b.rejected_total}
                  </div>
                </div>
              </div>

              {b.top_keys.length > 0 && (
                <div className="pt-1 border-t border-zinc-800/50 space-y-0.5">
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500">
                    Top hitters
                  </div>
                  {b.top_keys.slice(0, 3).map((k) => (
                    <div
                      key={k.key}
                      data-testid={`rate-limit-top-${b.name}-${k.key.replace(/[^a-zA-Z0-9]/g, "-")}`}
                      className="flex items-center justify-between font-mono text-[9px]"
                    >
                      <span className="truncate text-zinc-500">{k.key}</span>
                      <span className="tabular-nums text-zinc-400">
                        <span className={k.remaining === 0 ? "text-red-400" : ""}>
                          {k.used}
                        </span>
                        <span className="text-zinc-600">/{b.limit}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Iter 87 batch-4 — per-bucket alert threshold override.
                  Only rendered when the global threshold is loaded so
                  admins see the fallback value inline. */}
              {threshold != null && (
                <div
                  data-testid={`bucket-threshold-row-${b.name}`}
                  className="pt-1 border-t border-zinc-800/50 flex items-center gap-1.5"
                >
                  <span className="text-[9px] uppercase tracking-wider text-zinc-500 shrink-0">
                    Alert if +
                  </span>
                  <Input
                    data-testid={`bucket-threshold-input-${b.name}`}
                    type="number"
                    min={1}
                    max={10000}
                    placeholder={String(threshold)}
                    value={perBucketInputs[b.name] ?? (perBucketOverrides[b.name] != null ? String(perBucketOverrides[b.name]) : "")}
                    onChange={(e) => setPerBucketInputs((p) => ({ ...p, [b.name]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") handleSaveBucketThreshold(b.name); }}
                    className="h-5 border-zinc-800 bg-zinc-950 text-[10px] font-mono flex-1 min-w-0"
                  />
                  <span className="text-[9px] uppercase tracking-wider text-zinc-500 shrink-0">
                    /60s
                  </span>
                  <Button
                    data-testid={`bucket-threshold-save-${b.name}`}
                    size="sm"
                    variant="outline"
                    disabled={savingBucket === b.name}
                    onClick={() => handleSaveBucketThreshold(b.name)}
                    className="h-5 border-zinc-800 bg-zinc-950 px-1.5 text-[8px] uppercase tracking-wider text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
                  >
                    Save
                  </Button>
                  {perBucketOverrides[b.name] != null && (
                    <Button
                      data-testid={`bucket-threshold-clear-${b.name}`}
                      size="sm"
                      variant="outline"
                      disabled={savingBucket === b.name}
                      onClick={() => handleClearBucketOverride(b.name)}
                      className="h-5 border-zinc-800 bg-zinc-950 px-1.5 text-[8px] uppercase tracking-wider text-amber-400 hover:bg-zinc-900 hover:text-amber-200"
                      aria-label={`Clear ${b.name} threshold override (falls back to ${threshold})`}
                    >
                      ×
                    </Button>
                  )}
                  <Button
                    data-testid={`bucket-threshold-history-${b.name}`}
                    size="sm"
                    variant="outline"
                    onClick={() => handleLoadHistory(b.name)}
                    className="h-5 border-zinc-800 bg-zinc-950 px-1.5 text-[8px] uppercase tracking-wider text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200"
                    aria-label={`Show ${b.name} threshold history`}
                  >
                    {historyBucket === b.name ? "hide" : "log"}
                  </Button>
                </div>
              )}

              {/* Iter 87 batch-5 — inline history drawer. Iter 87
                  batch-6 — Export CSV + Undo latest change buttons. */}
              {historyBucket === b.name && (
                <div
                  data-testid={`bucket-threshold-history-drawer-${b.name}`}
                  className="pt-1 border-t border-zinc-800/50 space-y-0.5"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[9px] uppercase tracking-wider text-zinc-500">
                      Threshold history
                    </div>
                    <div className="flex gap-1">
                      <Button
                        data-testid={`bucket-threshold-history-export-${b.name}`}
                        size="sm"
                        variant="outline"
                        disabled={historyEntries.length === 0}
                        onClick={handleExportHistoryCsv}
                        className="h-4 border-zinc-800 bg-zinc-950 px-1 text-[8px] uppercase tracking-wider text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200"
                      >
                        CSV
                      </Button>
                      {undoConfirmBucket === b.name ? (
                        <div
                          data-testid={`bucket-threshold-history-undo-confirm-${b.name}`}
                          className="flex items-center gap-1"
                        >
                          <span className="text-[8px] uppercase tracking-wider text-amber-400">
                            Undo?
                          </span>
                          <Button
                            data-testid={`bucket-threshold-history-undo-yes-${b.name}`}
                            size="sm"
                            variant="outline"
                            disabled={historyEntries.length === 0}
                            onClick={() => handleUndoThresholdChange(b.name)}
                            className="h-4 border-emerald-800 bg-zinc-950 px-1 text-[8px] uppercase tracking-wider text-emerald-400 hover:bg-emerald-950 hover:text-emerald-200"
                          >
                            Yes
                          </Button>
                          <Button
                            data-testid={`bucket-threshold-history-undo-no-${b.name}`}
                            size="sm"
                            variant="outline"
                            onClick={() => setUndoConfirmBucket(null)}
                            className="h-4 border-zinc-800 bg-zinc-950 px-1 text-[8px] uppercase tracking-wider text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200"
                          >
                            No
                          </Button>
                        </div>
                      ) : (
                        <Button
                          data-testid={`bucket-threshold-history-undo-${b.name}`}
                          size="sm"
                          variant="outline"
                          disabled={historyEntries.length === 0}
                          onClick={() => handleUndoClick(b.name)}
                          className="h-4 border-zinc-800 bg-zinc-950 px-1 text-[8px] uppercase tracking-wider text-amber-500 hover:bg-zinc-900 hover:text-amber-300"
                          aria-label={`Undo latest ${b.name} threshold change`}
                        >
                          ↶ Undo
                        </Button>
                      )}
                    </div>
                  </div>
                  {historyEntries.length === 0 ? (
                    <div className="text-[9px] text-zinc-600 italic">No changes recorded yet</div>
                  ) : (
                    historyEntries.map((h, i) => (
                      <div
                        key={i}
                        data-testid={`bucket-threshold-history-row-${b.name}-${i}`}
                        className="flex items-center justify-between font-mono text-[9px]"
                      >
                        <span className="text-zinc-500">
                          {h.from ?? "∅"} → {h.to ?? "global"}
                        </span>
                        <span className="text-zinc-600">
                          {formatRelativeTime(new Date(h.changed_at).getTime())}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Iter 87 batch-3 — Alert Throttle Config + Digest Preview
          bar. Both admin-only utilities live in one row so ops can
          reach them without scrolling. */}
      <div className="pt-3 border-t border-zinc-800 grid grid-cols-1 md:grid-cols-2 gap-3">
        {threshold != null && (
          <div
            data-testid="alert-threshold-config"
            className="rounded-sm border border-zinc-800/70 bg-zinc-900/40 p-2 space-y-1.5"
          >
            <div className="flex items-baseline justify-between">
              <div className="text-[9px] uppercase tracking-widest text-zinc-500">
                Spike Alert Threshold
              </div>
              <div
                data-testid="alert-threshold-current"
                className="text-[9px] tabular-nums text-zinc-400"
              >
                current: {threshold} / 60s
              </div>
            </div>
            <div className="flex gap-1.5">
              <Input
                data-testid="alert-threshold-input"
                type="number"
                min={1}
                max={10000}
                value={thresholdInput}
                onChange={(e) => setThresholdInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSaveThreshold(); }}
                className="h-7 border-zinc-800 bg-zinc-950 text-[10px] font-mono"
              />
              <Button
                data-testid="alert-threshold-save"
                size="sm"
                variant="outline"
                disabled={savingThreshold}
                onClick={handleSaveThreshold}
                className="h-7 border-zinc-800 bg-zinc-950 px-2 text-[9px] uppercase tracking-wider text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
              >
                {savingThreshold ? "…" : "Save"}
              </Button>
            </div>
          </div>
        )}

        <div
          data-testid="digest-preview-search"
          className="rounded-sm border border-zinc-800/70 bg-zinc-900/40 p-2 space-y-1.5"
        >
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">
            Preview Digest For User
          </div>
          <div className="flex gap-1.5">
            <Input
              data-testid="digest-preview-user-id"
              placeholder="Paste user ObjectId…"
              value={digestUserId}
              onChange={(e) => setDigestUserId(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handlePreviewDigest(); }}
              className="h-7 border-zinc-800 bg-zinc-950 text-[10px] font-mono"
            />
            <Button
              data-testid="digest-preview-open"
              size="sm"
              variant="outline"
              onClick={handlePreviewDigest}
              className="h-7 border-zinc-800 bg-zinc-950 px-2 text-[9px] uppercase tracking-wider text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
            >
              Preview
            </Button>
          </div>
          {/* Iter 87 batch-4 — recent-user chips. Click to
              re-populate the input; the modal auto-opens on click
              so ops can flip through digests without re-typing. */}
          {recentDigestUsers.length > 0 && (
            <div
              data-testid="digest-preview-recent"
              className="flex flex-wrap gap-1 pt-0.5"
            >
              <span className="text-[8px] uppercase tracking-wider text-zinc-600 self-center mr-1">
                recent:
              </span>
              {recentDigestUsers.map((entry) => {
                const uid = entry.uid;
                const at = entry.at;
                return (
                  <button
                    key={uid}
                    data-testid={`digest-preview-recent-${uid}`}
                    data-at={at || ""}
                    onClick={() => {
                      setDigestUserId(uid);
                      // Move this entry to the front of the recents with a fresh timestamp.
                      const nowMs = Date.now();
                      setRecentDigestUsers((prev) => {
                        const next = [{ uid, at: nowMs }, ...prev.filter((e) => e.uid !== uid)].slice(0, 5);
                        try { localStorage.setItem("stratos.rl.recent-digest-users", JSON.stringify(next)); } catch {}
                        return next;
                      });
                      setDigestOpen(true);
                    }}
                    className="rounded-sm border border-zinc-800 bg-zinc-950 px-1.5 py-0.5 font-mono text-[9px] text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200 flex items-center gap-1"
                    title={`Preview digest for ${uid}${at ? ` · viewed ${new Date(at).toLocaleString()}` : ""}`}
                  >
                    {(() => {
                      const entry = userNames[uid];
                      const nm = typeof entry === "string" ? entry : entry?.name;
                      if (!nm || nm === "unknown") return null;
                      return (
                        <span
                          data-testid={`digest-preview-recent-name-${uid}`}
                          className="text-zinc-200 font-sans normal-case"
                        >
                          {nm}
                        </span>
                      );
                    })()}
                    <span>{uid.length > 8 ? `…${uid.slice(-6)}` : uid}</span>
                    {at && (
                      <span
                        data-testid={`digest-preview-recent-age-${uid}`}
                        className="text-zinc-600 text-[8px]"
                      >
                        · {formatRelativeTime(at)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <WeeklyRecapPreviewModal
        open={digestOpen}
        onOpenChange={setDigestOpen}
        impersonateUserId={digestUserId.trim() || null}
      />
    </div>
  );
}
